import { CartContext } from '@context/cart.context';
import Button from '@components/atoms/button/button';
import { useNegotiation } from '@context/negotiation.context';
import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { Negotiation } from '@interfaces/models/negotiation';
import { useContextSelector } from 'use-context-selector';
import clsx from 'clsx';
import useProductQuery from '@hooks/product/use-product-query';
import usePdpLayout from '@hooks/product/use-pdp-layout';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import styles from '../CTA-product.module.scss';

export type CartButtonProps = {
  triggerAddToCart?: boolean;
  className?: string;
};

const CartButton: React.FC<CartButtonProps> = (props): React.JSX.Element => {
  const { triggerAddToCart, className } = props;
  const { t } = useTranslation();
  const { negotiation: contextNegotiation } = useNegotiation();
  const { product, isProductBuyable, isSellerOnVacation } = useProductQuery();
  const { isHeroPdpStyles } = useHeroPdpStyles();
  const { isRecirculation } = usePdpLayout();
  const productsInCart = useContextSelector(CartContext, (v) => v.productsInCart);
  const isProductIdInCart = useContextSelector(CartContext, (v) => v.isProductIdInCart);
  const isCartProcessing = useContextSelector(CartContext, (v) => v.isCartProcessing);
  const addToShoppingCart = useContextSelector(CartContext, (v) => v.addToShoppingCart);

  const negotiation = useMemo<Negotiation | null>(() => {
    return contextNegotiation?.isClosed ? null : contextNegotiation;
  }, [contextNegotiation]);

  // Add to cart button is disabled unless you accept the negotiation offer
  // TODO: check functionality of this function with various situations - based on dependencies
  const isAddToCartButtonDisabled = useMemo<boolean>(() => {
    if (negotiation?.isAccepted) {
      return false;
    }

    return !isProductBuyable || isSellerOnVacation || isCartProcessing || isProductIdInCart(productsInCart, product.id);
  }, [isCartProcessing, product, productsInCart, isSellerOnVacation, negotiation]);

  useEffect(() => {
    if (triggerAddToCart) {
      addToShoppingCart(product, true);
    }
  }, [triggerAddToCart]);

  return (
    <Button
      variant="primary"
      size={isRecirculation || isHeroPdpStyles ? 'medium' : 'large'}
      fullWidth
      data-cy="pdp_buy_btn"
      data-vc-dd-action-name="pdp_buy_button"
      disabled={isAddToCartButtonDisabled}
      onClick={() => {
        addToShoppingCart(product, true);
      }}
      className={clsx(
        styles.CTAButtonsBlock__button,
        className,
        (isRecirculation || isHeroPdpStyles) && 'vc-btn--large--md-up',
      )}
    >
      {negotiation && negotiation.isAccepted
        ? t('PRODUCT.DETAILS.CTA.BUY_WITH_PRICE', { negotiation })
        : t('PRODUCT.DETAILS.CTA.BUY')}
    </Button>
  );
};

export default CartButton;
